import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Zap,
  RefreshCw,
  Search,
  X,
  Plus,
  Bot,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skill, skillsApi } from '@/services/api/agentApi';
import {
  SkillCard,
  StatusFilter,
  DomainFilter,
} from './SkillsTab';
import * as errorUtils from '@/utils/errors';

interface SkillsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, each skill card shows an "Insert" button that triggers this callback. */
  onSkillInsert?: (skill: Skill) => void;
}

function isStatusFilter(value: string): value is StatusFilter {
  return value === 'all' || value === 'draft' || value === 'active' || value === 'canary' || value === 'deprecated';
}

function isDomainFilter(value: string): value is DomainFilter {
  return value === 'all' || value === 'data' || value === 'workflow' || value === 'general';
}

export const SkillsModal: React.FC<SkillsModalProps> = ({ open, onOpenChange, onSkillInsert }) => {
  const [activeTab, setActiveTab] = useState<'browse' | 'create'>('browse');
  const [skills, setSkills] = useState<Skill[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [activating, setActivating] = useState<string | null>(null);

  // Create form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [domain, setDomain] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const resetCreateForm = () => {
    setName(''); setDescription(''); setInstructions('');
    setDomain(''); setTagsInput(''); setCreateError(null);
  };

  const fetchSkills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (domainFilter !== 'all') params.domain = domainFilter;
      const result = await skillsApi.list(params);
      const data = result?.data;
      setSkills(data?.skills || []);
      setTotal(data?.total || 0);
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to load skills'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, domainFilter]);

  useEffect(() => {
    if (open) fetchSkills();
  }, [open, fetchSkills]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setActiveTab('browse');
      resetCreateForm();
      setSearchInput('');
    }
  }, [open]);

  const handleActivate = async (skillId: string) => {
    setActivating(skillId);
    try {
      await skillsApi.activate(skillId);
      fetchSkills();
    } catch { /* silent */ } finally {
      setActivating(null);
    }
  };

  const handleDeprecate = async (skillId: string) => {
    setActivating(skillId);
    try {
      await skillsApi.deprecate(skillId);
      fetchSkills();
    } catch { /* silent */ } finally {
      setActivating(null);
    }
  };

  const handleCreate = async () => {
    if (!name.trim() || !description.trim() || !instructions.trim()) {
      setCreateError('Name, description, and instructions are required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
      await skillsApi.create({
        name: name.trim(),
        description: description.trim(),
        instructions: instructions.trim(),
        domain: domain || undefined,
        tags: tags.length > 0 ? tags : undefined,
      });
      resetCreateForm();
      setActiveTab('browse');
      fetchSkills();
    } catch (err: unknown) {
      setCreateError(errorUtils.getApiErrorMessage(err, 'Failed to create skill'));
    } finally {
      setCreating(false);
    }
  };

  const filtered = useMemo(() => {
    if (!searchInput.trim()) return skills;
    const q = searchInput.toLowerCase();
    return skills.filter(
      s => s.name.toLowerCase().includes(q)
        || s.description.toLowerCase().includes(q)
        || s.tags.some(t => t.toLowerCase().includes(q)),
    );
  }, [skills, searchInput]);

  const stats = useMemo(() => {
    const active = skills.filter(s => s.status === 'active').length;
    const draft = skills.filter(s => s.status === 'draft').length;
    const agentProposed = skills.filter(s => s.origin === 'agent').length;
    return { active, draft, agentProposed };
  }, [skills]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="sm:max-w-3xl p-0 gap-0">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <Zap className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-card-foreground">
              Skills
              {total > 0 && (
                <span className="text-xs font-normal text-muted-foreground ml-1.5">({total})</span>
              )}
            </h2>
            <p className="text-[11px] text-muted-foreground">Browse and manage reusable agent skills</p>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            if (value === 'browse' || value === 'create') {
              setActiveTab(value);
            }
          }}
        >
          {/* Tab bar */}
          <div className="px-4 pb-3">
            <TabsList className="w-full h-9">
              <TabsTrigger value="browse" className="flex-1 text-xs">Browse skills</TabsTrigger>
              <TabsTrigger value="create" className="flex-1 text-xs">Create skill</TabsTrigger>
            </TabsList>
          </div>

          {/* ── Browse Tab ── */}
          <TabsContent value="browse" className="data-[state=active]:h-[60vh] data-[state=inactive]:h-0 overflow-y-auto mt-0">
            {/* Toolbar — filters + search */}
            <div className="border-b border-border px-4 py-3 space-y-3">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={fetchSkills} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  className="text-sm border border-border rounded-md px-2 py-1 bg-background text-foreground"
                  value={statusFilter}
                  onChange={(e) => {
                    if (isStatusFilter(e.target.value)) {
                      setStatusFilter(e.target.value);
                    }
                  }}
                >
                  <option value="all">All statuses</option>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="canary">Canary</option>
                  <option value="deprecated">Deprecated</option>
                </select>

                <select
                  className="text-sm border border-border rounded-md px-2 py-1 bg-background text-foreground"
                  value={domainFilter}
                  onChange={(e) => {
                    if (isDomainFilter(e.target.value)) {
                      setDomainFilter(e.target.value);
                    }
                  }}
                >
                  <option value="all">All domains</option>
                  <option value="data">Data</option>
                  <option value="workflow">Workflow</option>
                  <option value="general">General</option>
                </select>

                <div className="relative flex-1 min-w-[160px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search skills..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    className="w-full pl-9 pr-8 py-1.5 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  {searchInput && (
                    <button
                      onClick={() => setSearchInput('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-4 px-4 py-2.5 border-b border-border text-xs">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-muted-foreground">Active:</span>
                <span className="font-medium text-card-foreground">{stats.active}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-slate-400" />
                <span className="text-muted-foreground">Draft:</span>
                <span className="font-medium text-card-foreground">{stats.draft}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Bot className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Agent-proposed:</span>
                <span className="font-medium text-card-foreground">{stats.agentProposed}</span>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mx-4 mt-3 flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Skills list */}
            {loading && skills.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : !loading && skills.length === 0 && !error ? (
              <div className="flex flex-col items-center justify-center text-muted-foreground py-16 px-8">
                <Zap className="w-12 h-12 mb-3 opacity-40" />
                <h3 className="text-base font-medium text-card-foreground mb-1">No skills yet</h3>
                <p className="text-sm text-center mb-4 max-w-sm">
                  Skills are reusable instructions that agents follow for specific tasks.
                  Create one manually or let agents propose them from successful workflows.
                </p>
                <Button
                  size="sm"
                  onClick={() => setActiveTab('create')}
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Create skill
                </Button>
              </div>
            ) : (
              <div className="p-4 grid gap-4 grid-cols-1">
                {filtered.map(skill => (
                  <SkillCard
                    key={skill.skill_id}
                    skill={skill}
                    onActivate={handleActivate}
                    onDeprecate={handleDeprecate}
                    activating={activating}
                    onInsert={onSkillInsert ? () => { onSkillInsert(skill); onOpenChange(false); } : undefined}
                  />
                ))}
                {filtered.length === 0 && skills.length > 0 && (
                  <div className="text-center text-sm text-muted-foreground py-8">
                    No skills match your search.
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* ── Create Tab ── */}
          <TabsContent value="create" className="data-[state=active]:h-[60vh] data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4">
            <div className="space-y-5">
              <p className="text-xs text-muted-foreground">
                Define a reusable skill that agents can follow for specific tasks. Skills created here start as drafts and can be activated from the Browse tab.
              </p>

              {/* Skill Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">Skill name</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Weekly Revenue Report"
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">Description</label>
                <p className="text-xs text-muted-foreground">A short summary of what this skill does. This helps agents decide when to use it.</p>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="e.g. Generates a weekly revenue summary from the data warehouse with trend analysis..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>

              {/* Instructions */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">Instructions</label>
                <p className="text-xs text-muted-foreground">Step-by-step instructions the agent will follow when executing this skill.</p>
                <textarea
                  value={instructions}
                  onChange={e => setInstructions(e.target.value)}
                  placeholder={"1. Query the revenue collection for the past 7 days\n2. Group results by product category\n3. Calculate week-over-week growth percentage\n4. Format as a summary table with highlights"}
                  rows={6}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none font-mono"
                />
              </div>

              {/* Domain & Tags */}
              <div className="flex gap-4">
                <div className="flex-1 space-y-2">
                  <label className="text-sm font-medium text-card-foreground">Domain</label>
                  <select
                    value={domain}
                    onChange={e => setDomain(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground"
                  >
                    <option value="">General</option>
                    <option value="data">Data</option>
                    <option value="workflow">Workflow</option>
                  </select>
                </div>
                <div className="flex-1 space-y-2">
                  <label className="text-sm font-medium text-card-foreground">Tags</label>
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={e => setTagsInput(e.target.value)}
                    placeholder="revenue, weekly, report"
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <p className="text-xs text-muted-foreground">Comma-separated tags for search and categorization.</p>
                </div>
              </div>

              {/* Error */}
              {createError && (
                <div className="flex items-start gap-2 p-2 bg-destructive/10 border border-destructive/20 rounded-md">
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                  <p className="text-xs text-destructive">{createError}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { resetCreateForm(); setActiveTab('browse'); }} className="border-border text-card-foreground hover:bg-muted">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={creating}
                  className="gap-1.5"
                >
                  {creating ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                  Create skill
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
